import { useEffect, useState } from 'react';
import {
    Typography,
    Box,
    Paper,
    Grid,
    Stack,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Tooltip,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button
} from '@mui/material';
import { QuizTypes } from 'consts';
import {
    QuizSessionOverview_quiz_master_quiz_session_quiz_session_user_progresses as UserSession,
    QuizSessionOverview_quiz_master_quiz_session_quiz_session_user_answers as UserAnswer,
    QuizSessionOverview_quiz_master_quiz_session_session_progress as SessionProgress
} from 'graphql/subscriptions/types/QuizSessionOverview';
import { getFormattedDateTime, getPlayDuration, getDurationString, getDurationMs } from './overview-tools';
import { UserSessionDisplayData } from './quiz-session-overview';
import { useQuizSessionContext, useQuizContext } from 'contexts';

import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EnhancedTable, Spinner, TableHeadCell, ToggleSwitch, useSnackbar } from 'components';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { getQuizQuestions } from 'graphql/queries';
import { QuizQuestions, QuizQuestionsVariables } from 'graphql/queries/types/QuizQuestions';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { updateUserAnswerAcceptance } from 'graphql/mutations';
import { UpdateUserAnswerAcceptance, UpdateUserAnswerAcceptanceVariables } from 'graphql/mutations/types/UpdateUserAnswerAcceptance';

interface QuestionUserAnswerRow {
    id?: number;
    rowKey: string;
    userDisplayName: string;
    userId: number;
    userAnswer?: string;
    isCorrect?: boolean;
    questionIndex: number;
}

interface Props {
    userSessions: UserSession[];
    userAnswers: UserAnswer[];
    sessionProgress?: SessionProgress;
}

export const SlideshowPaperlessOverview = (props: Props) => {
    const sessionContext = useQuizSessionContext();
    const { quiz } = useQuizContext();
    const snackbar = useSnackbar();
    const [sessionOverviewVisible, setSessionOverviewVisibility] = useState(true);
    const [questionAnswersVisible, setQuestionAnswerVisibility] = useState(true);
    const currentPresentationQuestion = props.sessionProgress?.progressIndex ?? -1;
    const [viewQuestionIndex, setQuestionIndex] = useState(currentPresentationQuestion);
    const [sessionUsers, setSessionUsers] = useState<
        {
            id: number;
            displayName: string;
        }[]
    >([]);

    useEffect(() => {
        setQuestionIndex(props.sessionProgress?.progressIndex ?? -1);
    }, [props.sessionProgress]);

    useEffect(() => {
        setSessionUsers(
            props.userSessions.map((u) => ({
                id: u.userId,
                displayName: u.user.displayName
            }))
        );
    }, [props.userSessions]);

    const tableHeaderCells: TableHeadCell<QuestionUserAnswerRow>[] = [
        {
            id: 'userDisplayName',
            label: 'User'
        },
        {
            id: 'userAnswer',
            label: 'User Answer'
        }
    ];

    const onMutationError = (error: ApolloError) => {
        console.error(error);
        snackbar.error('Failed to update answer. Please try again...');
    };

    const onAnswerModified = () => {
        snackbar.success('Answer updated');
    };

    const [updateAnswerIsCorrect, { loading: executingMutation }] = useMutation<
        UpdateUserAnswerAcceptance,
        UpdateUserAnswerAcceptanceVariables
    >(updateUserAnswerAcceptance, {
        onCompleted: onAnswerModified,
        onError: onMutationError
    });

    const {
        // loading,
        // error,
        data: quizQuestions
        // refetch
    } = useQuery<QuizQuestions, QuizQuestionsVariables>(getQuizQuestions, {
        variables: { quizId: quiz.id! }
    });

    const getPreparedData = (userSessions: UserSession[], userAnswers: UserAnswer[], currentPresentedQIndex: number): UserSessionDisplayData[] => {
        // enrich user session data
        var uss = userSessions.map((us) => {
            const cUserAnswers = userAnswers.filter((ua) => (ua.userId === us.userId));
            const correctAnswers = cUserAnswers.filter((a) => a.isCorrect).length;
            const incorrectAnswers = currentPresentedQIndex - correctAnswers + 1;
            const lastAnswer = cUserAnswers.sort(a => a.answeredAt).at(cUserAnswers.length-1);

            return {
                id: us.id,
                userDisplayName: us.user.displayName,
                correctAnswers: correctAnswers,
                incorrectAnswers: incorrectAnswers,
                timeStarted: getFormattedDateTime(us.timeStarted),
                timeLastAnswer: getFormattedDateTime(lastAnswer?.answeredAt),
                playDuration: getPlayDuration(us.timeStarted, lastAnswer?.answeredAt),
                playDurationMs: getDurationMs(us.timeStarted, lastAnswer?.answeredAt),
                hasEndedSession: us.hasEnded,
                additionalInfo: us.additionalInfo
            };
        });

        // sort participants by score
        var sortedUserSessions = uss.sort((usa, usb) => {
            // the more correct answers you have the better you total position is
            if (usa.correctAnswers !== usb.correctAnswers) {
                return usb.correctAnswers - usa.correctAnswers;
            }

            // the less incorrect answers you have the better you total position is
            if ( usa.incorrectAnswers !== usb.incorrectAnswers) {
                return  usa.incorrectAnswers - usb.incorrectAnswers;
            }

            // the less time you took answering questions better you total position is
            if ( usa.playDurationMs !== usb.playDurationMs) {
                return  usa.playDurationMs - usb.playDurationMs;
            }

            return 0;
        });

        return sortedUserSessions;
    };

    const userSessions = getPreparedData(props.userSessions, props.userAnswers, currentPresentationQuestion);

    // sort answered questions alphabetically
    const userAnswerTableData: QuestionUserAnswerRow[] = sessionUsers
        .map<QuestionUserAnswerRow>((u, index) => {
            const answer = props.userAnswers.find((ua) => ua.questionIndex === viewQuestionIndex && ua.userId === u.id);
            return {
                id: answer?.id,
                rowKey: answer?.id ? `answer-${answer.id}` : `unanswered-${index}`,
                questionIndex: viewQuestionIndex,
                isCorrect: answer?.isCorrect,
                userAnswer: answer?.answer ?? undefined,
                userDisplayName: u.displayName,
                userId: u.id
            };
        })
        .sort((a, b) => (a.userDisplayName ?? '').localeCompare(b.userDisplayName ?? ''));

    const currentQuestion = quizQuestions?.quiz_master_quizzes_by_pk?.questions.at(viewQuestionIndex);

    const toggleSessionOverviewVisibility = () => {
        setSessionOverviewVisibility((visibility) => !visibility);
    };

    const toggleQuestionAnswerVisibility = () => {
        setQuestionAnswerVisibility((visibility) => !visibility);
    };

    const changeViewedQuestion = (indexChange: number) => {
        setQuestionIndex((index) => {
            const futureIndex = index + indexChange;
            if (futureIndex < 0 || futureIndex > (quizQuestions?.quiz_master_quizzes_by_pk?.questions.length ?? 0)) {
                return index;
            }
            return futureIndex;
        });
    };

    const onUserAnswerToggle = (answerRow: QuestionUserAnswerRow) => {
        if (!answerRow.id) {
            snackbar.warning('Cannot toggle a non-existant answer');
            return;
        }

        updateAnswerIsCorrect({
            variables: {
                userAnswerId: answerRow.id,
                isCorrect: !answerRow.isCorrect
            }
        });
    };

    return (
        <Box className="aq-content" sx={{height: "auto"}}>
            <Stack spacing={2}>
                <Typography variant="h2" className="quiz-title">
                    {quiz.name}
                </Typography>
                <Box>
                    <Paper sx={{ padding: 5 }}>
                        <Typography variant="h4" sx={{ marginBottom: 5 }}>
                            Quiz settings
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography textAlign="start">
                                    <b>Type:</b> {QuizTypes[quiz.quizTypeId]}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography textAlign="start">
                                    <b>Time limits</b>
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Stack spacing={1} direction="row">
                                    <Typography textAlign="start">
                                        <b>Total:</b>
                                    </Typography>
                                    {sessionContext?.session.maxDurationSec ? (
                                        <Typography>{getDurationString(sessionContext?.session.maxDurationSec, false)}</Typography>
                                    ) : (
                                        <AllInclusiveIcon fontSize="medium" />
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item xs={4}>
                                <Stack spacing={1} direction="row">
                                    <Typography textAlign="start">
                                        <b>Question:</b>
                                    </Typography>
                                    {sessionContext?.session.questionTimeSec ? (
                                        <Typography>{getDurationString(sessionContext?.session.questionTimeSec, false)}</Typography>
                                    ) : (
                                        <AllInclusiveIcon fontSize="medium" />
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
                <Accordion expanded={sessionOverviewVisible} onChange={toggleSessionOverviewVisibility}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="session-overview-accordion-content"
                        id="questions-accordion-header"
                    >
                        <Typography>Session Overview</Typography>
                    </AccordionSummary>
                    <AccordionDetails id="session-overview-accordion-content">
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="User session overview table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell variant="head" align="center">
                                            User
                                        </TableCell>
                                        <TableCell variant="head" align="center">
                                            Correct Answers
                                        </TableCell>
                                        <TableCell variant="head" align="center">
                                            Incorrect or Skipped
                                        </TableCell>
                                        <TableCell variant="head" align="center">
                                            Time Started
                                        </TableCell>
                                        <TableCell variant="head" align="center">
                                            Time of Last Answer
                                        </TableCell>
                                        <TableCell variant="head" align="center">
                                            Total Play Time
                                        </TableCell>
                                        <TableCell variant="head" align="center">
                                            Is Playing
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {userSessions.map((row) => (
                                        <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell component="th" scope="row">
                                                {row.userDisplayName}
                                            </TableCell>
                                            <TableCell align="center">{row.correctAnswers}</TableCell>
                                            <TableCell align="center">{row.incorrectAnswers}</TableCell>
                                            <TableCell align="center">{row.timeStarted}</TableCell>
                                            <TableCell align="center">{row.timeLastAnswer}</TableCell>
                                            <TableCell align="center">{row.playDuration}</TableCell>
                                            <TableCell align="center">
                                                {row.hasEndedSession ? (
                                                    <Tooltip title="Finished">
                                                        <SelfImprovementIcon />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Playing">
                                                        <DirectionsRunIcon />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
                <Accordion expanded={questionAnswersVisible} onChange={toggleQuestionAnswerVisibility}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="questions-accordion-content"
                        id="questions-accordion-header"
                    >
                        <Typography>Question Overview</Typography>
                    </AccordionSummary>
                    <AccordionDetails id="questions-accordion-content">
                        <Box sx={{ padding: 5 }}>
                            <Typography variant="h4" sx={{ marginBottom: 5 }}>
                                Question
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={2}>
                                    <Button className="slide-button left" onClick={() => changeViewedQuestion(-1)}>
                                        <KeyboardArrowLeftIcon fontSize="large" />
                                    </Button>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography textAlign="start">
                                        {currentQuestion ? `${viewQuestionIndex + 1}. ${currentQuestion?.question}` : ''}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Button className="slide-button right" onClick={() => changeViewedQuestion(1)}>
                                        <KeyboardArrowRightIcon fontSize="large" />
                                    </Button>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography textAlign="start">
                                        <b>Correct Answer:</b>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography textAlign="start">{currentQuestion?.answers.find((a) => a.isCorrect)?.answer}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                        <EnhancedTable<QuestionUserAnswerRow>
                            title="User Answers"
                            data={userAnswerTableData}
                            headerCells={tableHeaderCells}
                            dataRecordIdKey="rowKey"
                            defaultRowsPerPage={10}
                            customRowActions={[
                                {
                                    element: <ToggleSwitch />,
                                    onClick: onUserAnswerToggle,
                                    tooltip: 'Toggle whether answer is correct',
                                    rowValueKey: 'isCorrect',
                                    defaultValue: false
                                }
                            ]}
                            customActionsHeader="Is Correct"
                        />
                    </AccordionDetails>
                </Accordion>
            </Stack>
            {executingMutation && <Spinner />}
        </Box>
    );
};
